// The sky lab (`?scene=sky`, gate C3): the time of day and the weather on the stand-in world (stand-ins.ts), with the
// Wanderer standing in each shot. Its sim is the sky module (module.ts) and the ground's collider; its view is the
// sky's view (view.ts) on @rpg/look's low-poly look at the boot's tier. The handle (`__rpg.sky`) frames the six
// place-and-time shots, sets the hour, the weather, the rate and the wetness (all through the sim), reports the look,
// and runs @rpg/perf's tour over the six shots. Until the world stream's valley lands, this is where C3's shots and
// counts come from; the integrator re-takes them in the real valley.
import * as THREE from "three";
import { finite, type Invariant } from "@rpg/harness";
import type { EventBus } from "@rpg/kernel";
import type { BodySpec } from "@rpg/collision";
import { buildFigure, createMotion, type Figure, type FigureCard, type Motion } from "@rpg/figure";
import { createLowPolyLook, type LowPolyLook, type LowPolySceneLook, type LowPolyTier } from "@rpg/look";
import { overlay, perfReport, runTour, type TourSegment } from "@rpg/perf";
import type { Runtime } from "@rpg/runtime";
import type { SpikeContent } from "../content";
import type { GameEvents } from "../core";
import type { SceneDef } from "../sim/host";
import { LAB_ACTIONS } from "../sim/scene";
import { createSky, readSkyContent, SKY_ACTIONS, SKY_FAULTS, type Sky } from "./module";
import { buildStandIns, LAB_HALF, PLACES, skyLabHeight } from "./stand-ins";
import { createSkyView, type SkyView } from "./view";

/** The lab's planted defects. */
export const SKY_LAB_FAULTS = {
  /** At dusk the town's lanterns get a new material type, compiled after ready: the compile watchdog must trip. */
  lateDuskShader: false,
};

/** The sky's planted-defect names, as `?faults=` and `__rpg.faults()` take them. */
export const SKY_FAULT_NAMES = ["lateDuskShader", "skyNotSaved"] as const;

/** One shot: the hour and weather (and room), where the camera stands and looks, where the hero stands. */
export interface SkyShot {
  hours: number;
  weather: string;
  wet?: number;
  indoor?: string;
  eye: [number, number, number];
  target: [number, number, number];
  hero: [number, number, number];
  fov?: number;
}

const P = PLACES;
const g = skyLabHeight;
/** The six place-and-time pairs (the brief's, agent): heights are added on the ground at each point. */
export const SKY_SHOTS: Record<string, SkyShot> = {
  "meadow-dawn": { hours: 6.15, weather: "clear", eye: [P.meadow.x - 9, 2.1, P.meadow.z + 6], target: [P.meadow.x + 8, 2.2, P.meadow.z - 2], hero: [P.meadow.x - 3.2, P.meadow.z + 2.6, 1.9] },
  "forest-noon": { hours: 12, weather: "clear", eye: [P.forest.x + 8, 1.8, P.forest.z + 6], target: [P.forest.x - 3, 4.6, P.forest.z - 3], hero: [P.forest.x + 2.6, P.forest.z + 2.2, -2.3] },
  "town-dusk": { hours: 18.15, weather: "clear", eye: [P.town.x + 15, 2.2, P.town.z + 1.2], target: [P.town.x - 6, 2.4, P.town.z - 0.6], hero: [P.town.x + 9, P.town.z - 0.4, -1.4] },
  "ruins-night": { hours: 23, weather: "clear", eye: [P.ruins.x + 1, 2.6, P.ruins.z + 17], target: [P.ruins.x - 1, 3, P.ruins.z - 2], hero: [P.ruins.x + 1.5, P.ruins.z + 10.5, 0.3] },
  "town-rain": { hours: 14, weather: "rain", wet: 1, eye: [P.town.x - 14, 2.1, P.town.z - 1.2], target: [P.town.x + 6, 2.2, P.town.z + 0.4], hero: [P.town.x - 8, P.town.z + 0.3, 1.6] },
  interior: { hours: 20.5, weather: "clear", indoor: "cottage", eye: [P.cottage.x - 2.9, 1.75, P.cottage.z + 2.4], target: [P.cottage.x + 1.4, 1.0, P.cottage.z - 1.6], hero: [P.cottage.x + 0.4, P.cottage.z + 0.2, -0.6], fov: 62 },
};
export type SkyShotName = keyof typeof SKY_SHOTS;

/** The lab's sim scene: the sky module and the ground (a heightfield). */
export interface SkyLabScene {
  def: SceneDef;
  /** The sky once the host has set the scene up. */
  sky(): Sky;
}

/** The sky lab's sim scene from the content. */
export function skySceneFor(content: SpikeContent): SkyLabScene {
  applySkyFaultSwitches();
  const skyContent = readSkyContent(content);
  let sky: Sky | null = null;
  const ground = (): BodySpec => {
    const n = 35;
    const heights: number[] = [];
    for (let iz = 0; iz <= n; iz++) for (let ix = 0; ix <= n; ix++) heights.push(skyLabHeight((ix / n - 0.5) * LAB_HALF * 2, (iz / n - 0.5) * LAB_HALF * 2));
    return { id: "ground", kind: "fixed", colliders: [{ shape: { type: "heightfield", rows: n, cols: n, heights, size: { x: LAB_HALF * 2, y: 1, z: LAB_HALF * 2 } }, layer: "ground", tag: "grass" }] };
  };
  const def: SceneDef = {
    name: "sky",
    layers: { ground: [] },
    seed: 0x5c1e5,
    actions: LAB_ACTIONS,
    setup(collision, rng) {
      const s = createSky({ content: skyContent, rng });
      sky = s;
      const body = ground();
      return {
        parts: {
          modules: [
            s.module,
            {
              id: "sky-lab",
              version: "0.1.0",
              setup(ctx) {
                ctx.registerSystem(() => collision.step(), { name: "sky lab: physics step", order: 100 });
              },
            },
          ],
          content: {
            meta: { id: "sky-lab-scene", displayName: "Sky lab", version: "0.1.0" },
            requiresModules: [{ id: "sky", versionRange: "^0.1.0" }],
            build({ world }) {
              s.build(world);
              collision.add(body);
            },
          },
        },
        hashed: s.hashed,
        bodies: [body],
        beforeTick() {},
      };
    },
  };
  return {
    def,
    sky: () => {
      if (!sky) throw new Error("sky lab: not set up yet");
      return sky;
    },
  };
}

/** Turn on the sky's defects `?faults=` names. */
export function applySkyFaultSwitches(search = typeof location !== "undefined" ? location.search : ""): void {
  const on = (new URLSearchParams(search).get("faults") ?? "").split(",");
  if (on.includes("lateDuskShader")) SKY_LAB_FAULTS.lateDuskShader = true;
  if (on.includes("skyNotSaved")) SKY_FAULTS.skyNotSaved = true;
}

/** Mount the lab's view, handle and invariants once the host has set it up. */
export function mountSkyLab(o: {
  lab: SkyLabScene;
  runtime: Runtime;
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  content: SpikeContent;
  tier: string;
  events: EventBus<GameEvents>;
  dispatch: (action: string, payload: unknown) => void;
}) {
  const { runtime, renderer, scene, camera, content } = o;
  const reg = content.registry;
  const tierId = o.tier === "low" ? "low" : "high";
  const sceneLook = reg.get("looks", "valley-day") as unknown as LowPolySceneLook | undefined;
  const tierRow = reg.get("tiers", tierId) as unknown as LowPolyTier | undefined;
  if (!sceneLook || !tierRow) throw new Error("sky lab: no valley-day look or tier");
  const sky = o.lab.sky();
  // the look brings its own lights and sky: the placeholder ones go
  const old: THREE.Object3D[] = [];
  scene.traverse((c) => (c as THREE.Light).isLight && old.push(c));
  for (const l of old) l.removeFromParent();
  scene.background = null;
  camera.far = 400;
  camera.updateProjectionMatrix();

  let indoor: string | null = null;
  const view: SkyView = createSkyView({ content, scene, camera, renderer, tier: tierId, read: () => ({ hours: sky.hours(), day: sky.state().clock.day, weather: sky.blend(), indoor }) });
  const look: LowPolyLook = createLowPolyLook({ renderer, scene, camera, look: sceneLook, tier: tierRow, shadowExtent: 26, ...view.lookOptions });

  // the stand-in world on the look's one material; the lanterns their own mesh (the planted defect swaps it)
  const built = buildStandIns(look.palette);
  view.attach(look, built.shafts);
  const world = new THREE.Mesh(built.world, look.surface);
  world.name = "sky:world";
  const lanterns: THREE.Mesh<THREE.BufferGeometry, THREE.Material> = new THREE.Mesh(built.lanterns, look.surface);
  lanterns.name = "sky:lanterns";
  scene.add(world, lanterns);
  look.track(world);
  look.track(lanterns);

  // the Wanderer, standing at each shot's mark
  const row = reg.get("cards", "wanderer");
  if (!row) throw new Error("sky lab: no wanderer card");
  const card = row as unknown as FigureCard;
  const figure: Figure = buildFigure(card, { material: look.surface, colour: look.palette });
  figure.root.name = "figure:wanderer";
  scene.add(figure.root);
  look.track(figure.root, { blob: (row as { blob?: number }).blob });
  const motion: Motion = createMotion(figure, card.motion!, (x, z) => skyLabHeight(x, z));
  const focus = new THREE.Vector3();

  let shot: SkyShotName = "meadow-dawn";
  const frame = (name: SkyShotName): void => {
    const s = SKY_SHOTS[name]!;
    const [ex, ey, ez] = s.eye;
    const [tx, ty, tz] = s.target;
    camera.position.set(ex, g(ex, ez) + ey, ez);
    camera.fov = s.fov ?? 50;
    camera.updateProjectionMatrix();
    camera.lookAt(tx, g(tx, tz) + ty, tz);
    camera.updateMatrixWorld();
    const [hx, hz, yaw] = s.hero;
    figure.root.position.set(hx, g(hx, hz), hz);
    figure.root.rotation.y = yaw;
    motion.reset();
    focus.set((hx + tx) / 2, g(hx, hz), (hz + tz) / 2);
    indoor = s.indoor ?? null;
  };
  frame(shot);

  // the sky's events, for the specs (the last 200)
  const log: { event: "sky:time" | "sky:weather"; detail: string; step: number }[] = [];
  const note = (event: "sky:time" | "sky:weather", detail: string): void => {
    log.push({ event, detail, step: runtime.clock.step });
    if (log.length > 200) log.shift();
  };
  o.events.on("sky:time", (e) => note("sky:time", `${e.mark}@${e.day}`));
  o.events.on("sky:weather", (e) => note("sky:weather", `${e.from}>${e.to}`));
  // the planted defect: at dusk the lanterns get a material type nothing warmed
  o.events.on("sky:time", (e) => {
    if (e.mark === "dusk" && SKY_LAB_FAULTS.lateDuskShader && !(lanterns.material instanceof THREE.MeshPhongMaterial)) lanterns.material = new THREE.MeshPhongMaterial({ color: "#ffd27a", emissive: "#ffb040", vertexColors: true });
  });

  runtime.on(
    "late",
    (c) => {
      motion.update(Math.min(0.1, Math.max(0, c.dt)), { speed: 0, velocity: { x: 0, y: 0, z: 0 }, grounded: true, jump: false, glide: false, acting: false, lookAt: null });
      look.focus(focus);
      view.frame(c.dt, runtime.clock.time);
    },
    { order: 10, name: "sky lab: the look per time and weather" },
  );
  addEventListener("resize", () => {
    camera.aspect = innerWidth / innerHeight;
    camera.updateProjectionMatrix();
    look.resize(innerWidth, innerHeight);
  });
  // warm-up: every program the day and the weathers use (the shafts drawn, the rain falling) compiles before ready
  view.frame(0, 0);
  const back = view.forWarm();
  look.warm();
  back();

  const setShot = (name: SkyShotName): void => {
    const s = SKY_SHOTS[name];
    if (!s) throw new Error(`sky lab: no shot "${name}" (have ${Object.keys(SKY_SHOTS).join(", ")})`);
    shot = name;
    o.dispatch(SKY_ACTIONS.set, { hours: s.hours });
    o.dispatch(SKY_ACTIONS.weather, { id: s.weather, instant: true });
    o.dispatch(SKY_ACTIONS.wet, { wet: s.wet ?? 0 });
    frame(name);
  };

  const state = () => {
    const st = sky.state();
    return { shot, hours: st.clock.hours, day: st.clock.day, rate: st.clock.rate, period: sky.period(), weather: { ...st.weather }, blend: sky.blend(), view: view.report() };
  };

  const invariants: Invariant[] = [
    finite("sky", () => {
      const st = sky.state();
      return [st.clock.hours, st.clock.day, st.clock.rate, st.weather.wet, st.weather.since, st.weather.until];
    }),
    () => {
      const st = sky.state();
      const out: string[] = [];
      if (!(st.clock.hours >= 0 && st.clock.hours < 24)) out.push(`sky hours ${st.clock.hours} outside [0, 24)`);
      if (!(st.weather.wet >= 0 && st.weather.wet <= 1)) out.push(`sky wetness ${st.weather.wet} outside [0, 1]`);
      return out;
    },
  ];

  const handle = {
    /** The six shots' names. */
    shots: () => Object.keys(SKY_SHOTS),
    /** Frame a shot: its hour, weather and wetness set in the sim, the camera and the hero placed, its room's light. */
    shot: (name: SkyShotName) => setShot(name),
    /** Jump the clock forward to `hours` (marks on the way fire). */
    set: (hours: number) => o.dispatch(SKY_ACTIONS.set, { hours }),
    /** Force a weather (fading in, or `instant`). */
    weather: (id: string, instant = false) => o.dispatch(SKY_ACTIONS.weather, { id, instant }),
    /** The day's pace (1: the sky table's day length). */
    rate: (rate: number) => o.dispatch(SKY_ACTIONS.rate, { rate }),
    /** Set the ground's wetness. */
    wet: (wet: number) => o.dispatch(SKY_ACTIONS.wet, { wet }),
    /** The clock, the weather, its blend and what the view applied. */
    state,
    /** The look at any hour and weather (seam 4's `look.at`), as numbers. */
    lookAt: (hours: number, weather: string) => view.source.at(hours, weather),
    /** The sky's events so far (`sky:time` marks as "dusk@0", `sky:weather` changes as "clear>rain"), oldest first. */
    events: () => [...log],
    /** The low-poly look's report (tier, post passes, shadow map, blobs). */
    look: () => look.report(),
    /** The renderer's counts for the last frame. */
    counts: () => ({ calls: renderer.info.render.calls, triangles: renderer.info.render.triangles, programs: renderer.info.programs?.length ?? 0 }),
    /** The perf tour over the six shots (each held 1 s of game time; fast-forward `ffMs` game ms per drawn frame). */
    tour: (ffMs = 100) => {
      const ui = overlay(tierId);
      document.getElementById("perf-tour")?.setAttribute("data-no-strings", "");
      const t0 = { s: 0 };
      const segs: TourSegment[] = [{ name: "warmup", start: () => setShot("meadow-dawn"), tick: (t) => t - t0.s > 0.5, max: 3 }];
      for (const name of Object.keys(SKY_SHOTS) as SkyShotName[]) segs.push({ name, start: () => setShot(name), tick: (t) => t - t0.s > 1, max: 4 });
      for (const sg of segs) {
        const st = sg.start;
        sg.start = () => {
          t0.s = runtime.clock.time;
          st?.();
        };
      }
      runTour({
        kind: "sky-lab",
        renderer,
        scene,
        composer: look.composer,
        game: { simT: () => runtime.clock.time, pause: (on) => void (runtime.paused = on), advanceTime: (ms, draw) => void runtime.advance(ms, { draw: draw ?? true }) },
        segments: segs,
        ffMs,
        route: "sky-shots",
        estSeconds: 7,
        tier: tierId,
        ui,
        readyAt: performance.now(),
        quality: () => ({ ...look.report() }),
        extra: () => ({ sky: state() }),
      });
    },
    /** The perf tour's report (null before a tour; status "running", then "done"). */
    perf: () => perfReport(),
  };

  return {
    render: () => look.render(),
    handle,
    invariants,
    state,
    view,
    look,
  };
}

export type SkyLab = ReturnType<typeof mountSkyLab>;
